import { useAlert } from '../contexts/AlertContext';
import React, { useEffect, useState } from 'react';
import { apiClient } from '../services/api';
import { ConfirmButton } from '../components/ConfirmButton';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Project, FinancePlan, PpeInput, rowAmount } from '../types/project';
import { ppeTotals } from '../lib/ppe';
import { won } from '../utils/format';


/** 아직 아무 프로젝트에도 안 이어진 admin 프로젝트 — `GET /projects/ppe/candidates` */
interface AdminCandidate { id: number; name: string; startDate: string; endDate: string; contractValue: string; ppeAt: string | null }

interface Props {
  project: Project;
  plan: FinancePlan | null;
  onDecide: (outcome: '수주' | '실주', contractAmount: number | null) => Promise<void>;
  onRegisterPpe: (input: PpeInput) => Promise<void>;
  /** 이어진 admin 프로젝트의 빈 PPE 를 재무계획 숫자로 채운다 */
  onFillPpe: () => Promise<void>;
  /** 나라장터 결과 지금 확인 */
  onCheckBid: () => Promise<void>;
  saving: boolean;
}

/**
 * 수주 판정 — 이 화면에서 **유일하게 admin 으로 넘어가는 곳**이다.
 *
 * 재무계획 확정은 admin 을 건드리지 않는다. **사람이 「PPE 반영」을 눌렀을 때만** 간다.
 * 2026-09-28 에 **수주 전에도 누를 수 있게** 바꿨다 — admin 은 계약 전에 PPE 부터
 * 올린다(운영 9건 중 8건이 시작일보다 먼저 등록). 실주하면 버튼이 없다.
 * admin 에 이미 있는 건이면 새로 만들지 않고 번호로 잇는다.
 */
export const GateTab: React.FC<Props> = ({ project, plan, onDecide, onRegisterPpe, onFillPpe, onCheckBid, saving }) => {
  const { showConfirm } = useAlert();
  const [amount, setAmount] = useState<number>(
    project.contractAmount ?? project.proposalPrice ?? 0,
  );

  // 재무계획을 안 잡은 프로젝트면 계획이 비어 있다 — 프로젝트의 제안가로 떨어진다
  const price = plan?.proposalPrice || project.proposalPrice || 0;
  // PPE(만원, 2026-10-16)면 PPE 셈의 Operation Margin — 옛 계획은 인건비 · 직접경비
  const t = plan?.ppe ? ppeTotals(plan.ppe) : null;
  const labor = t ? t.manpower * 10000 : plan ? plan.manpower.reduce((s, r) => s + rowAmount(r), 0) : 0;
  const expense = t ? (t.cogs + t.indirect + t.direct) * 10000 : plan ? plan.expenses.travel + plan.expenses.outsourcing + plan.expenses.etc : 0;
  const margin = price > 0 ? ((price - labor - expense) / price) * 100 : 0;

  const decided = project.outcome !== null;
  const registered = project.ppeRegisteredAt !== null;
  const [ppe, setPpe] = useState({
    // 기간은 재무계획 PPE 의 기간으로 시작한다
    startDate: plan?.ppe?.startDate ?? '', endDate: plan?.ppe?.endDate ?? '', pm: project.pmName ?? '',
    // admin 은 만원 단위다. 계약금액(원)에서 옮긴다
    contractValue: Math.round((project.contractAmount ?? project.proposalPrice ?? 0) / 10000),
  });
  /* 판정에서 계약금액을 고치면 PPE 금액(만원)도 따라간다 — 전엔 처음 값이 남아 틀린 금액이 admin 으로 갈 수 있었다(#110) */
  useEffect(() => {
    if (project.contractAmount != null) setPpe((p) => ({ ...p, contractValue: Math.round(project.contractAmount! / 10000) }));
  }, [project.contractAmount]);
  const [linkId, setLinkId] = useState('');
  const canCreate = !!ppe.startDate && !!ppe.endDate && !!ppe.pm && ppe.contractValue >= 0;
  /* 「잇기」는 번호를 외워 적는 대신 목록에서 고른다(2026-10-07). PPE 가 있는 것만 이을 수 있다 */
  const [candidates, setCandidates] = useState<AdminCandidate[] | null>(null);
  useEffect(() => {
    if (registered || project.outcome === '실주') return;
    apiClient.get('/projects/ppe/candidates').then(setCandidates).catch(() => setCandidates([]));
  }, [registered, project.outcome]);
  /* 수주 · 실주는 되돌릴 수 없다 — 확인창으로 묻는다(2026-10-16) */

  return (
    <div className="space-y-6">
      {/* admin 경계 — 이 화면에서 제일 조심할 곳이다 */}
      <section className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-amber-900">여기서만 admin 에 기록이 갑니다</h2>
        <p className="mt-1 text-xs text-amber-800 leading-relaxed">
          아래 <b>PPE 반영</b>을 눌러야 admin 에 프로젝트와 PPE 가 생깁니다. 수주 전에도 누를 수 있고(admin 은 계약 전에
          PPE 부터 올립니다), 실주한 건은 가지 않습니다. 반영한 뒤 수익성 · CR · Cash Flow 는 admin 에서 봅니다.
        </p>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* 제안 원가안 — 읽기만 한다 */}
        <section className="bg-white rounded-lg border border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">
            제안 원가안
          </h2>
          <div className="px-4 py-3 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>제안가</span><span className="tabular-nums">{won(price)}</span></div>
            <div className="flex justify-between text-slate-600"><span>인건비</span><span className="tabular-nums">{won(labor)}</span></div>
            <div className="flex justify-between text-slate-600"><span>직접경비</span><span className="tabular-nums">{won(expense)}</span></div>
            <div className="flex justify-between pt-2 border-t border-slate-100 font-semibold text-slate-900">
              <span>예상 이익률</span>
              <span className="tabular-nums">{price > 0 ? `${margin.toFixed(1)}%` : '—'}</span>
            </div>
          </div>
        </section>

        {/* 계약 결과 */}
        <section className="bg-white rounded-lg border border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">
            계약 결과
          </h2>
          <div className="px-4 py-3 space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-slate-600">계약금액 <span className="text-xs text-slate-400">(VAT 포함)</span></span>
              {decided ? (
                <span className="tabular-nums font-semibold text-slate-900">{won(project.contractAmount)}</span>
              ) : (
                <input
                  type="number" min={0} step={1000000} value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-44 px-2 py-1 text-sm text-right border border-slate-200 rounded focus:border-indigo-400 focus:outline-none font-semibold"
                />
              )}
            </div>
            <div className="flex justify-between text-slate-600">
              <span>판정</span>
              <span className={`font-semibold ${
                project.outcome === '수주' ? 'text-emerald-700'
                : project.outcome === '실주' ? 'text-red-600' : 'text-slate-400'
              }`}>
                {project.outcome ?? '아직'}{project.outcome && project.bidResult?.manual ? ' (직접 판정)' : ''}
              </span>
            </div>
            {project.decidedAt && (
              <div className="flex justify-between text-slate-400 text-xs">
                <span>판정 일시</span>
                <span>{new Date(project.decidedAt).toLocaleString('ko-KR')}</span>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* 나라장터 결과 — 공고에서 담은 건은 사람이 판정하지 않는다(2026-10-16, 수주는 나라장터에서 정해진다) */}
      {project.bid && <BidResultPanel project={project} onCheck={onCheckBid} saving={saving} />}

      {/* 판정 — 직접 등록한 건은 사람이 누른다(「계약이 성사됐나」, 2026-10-22).
          공고 건도 누를 수 있다(2026-11-02) — 나라장터 결과가 안 잡힐 때(유찰 뒤 수의계약 · 차순위 협상 · 공동수급 등).
          누른 사람 · 시각이 남고, 나라장터 결과는 계속 확인해 위 칸에 같은지 다른지 보인다 */}
      {project.bid && !decided && project.stage === '제안서' && (
        <p className="text-right text-xs text-slate-500">
          보통은 나라장터 결과로 자동 판정됩니다(09 · 15시 · 「지금 확인」). 결과가 안 잡히거나 다를 때만 아래에서 직접 판정하세요 — 누가 판정했는지 남습니다.
        </p>
      )}
      {!decided && project.stage !== '제안서' ? (
        /* 판정은 제안서 단계에서 — 검토 · 재무계획에서 바로 수주하면 앞 단계를 건너뛴다(#110). 서버도 막는다 */
        <p className="text-right text-xs text-slate-500">계약 성사 여부는 제안서 단계에서 정합니다 — 지금은 {project.stage} 단계입니다.</p>
      ) : !decided ? (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={async () => await showConfirm(`계약이 무산된 것으로(실주) 처리하고 프로젝트를 종료로 보냅니다.${project.bid ? '\n나라장터 결과 대신 직접 판정합니다 — 판정한 사람 · 시각이 남습니다.' : ''}\n되돌릴 수 없습니다. 계속할까요?`) && onDecide('실주', null)}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            무산 (실주)
          </button>
          <button
            onClick={async () => await showConfirm(`계약금액 ${won(amount)} 으로 계약 성사(수주) 처리합니다.${project.bid ? '\n나라장터 결과 대신 직접 판정합니다 — 판정한 사람 · 시각이 남습니다.' : ''}\n되돌릴 수 없습니다. 계속할까요?`) && onDecide('수주', amount)}
            disabled={saving || amount <= 0}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            계약 성사 (수주)
          </button>
        </div>
      ) : project.outcome === '실주' ? (
        <p className="text-xs text-slate-500">
          실주했습니다. 프로젝트는 종료로 갔고 제안서 · 견적만 아카이브에 남습니다.
          <b> admin 에는 아무것도 만들지 않습니다.</b>
        </p>
      ) : null}

      {/* PPE 반영 — 실주가 아니면 판정 전에도 보인다. 자동으로 하지 않는다 */}
      {project.outcome !== '실주' && (
        <section className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">admin PPE 반영</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {registered
                  ? `${new Date(project.ppeRegisteredAt!).toLocaleString('ko-KR')} 에 반영했습니다. 이후 수정은 admin 에서 합니다.`
                  : 'admin 의 projects · PPE 에 등록합니다. 한 번만 누릅니다.'}
              </p>
            </div>
            {registered && (
              <span className="flex items-center gap-2 flex-shrink-0">
                {/* admin PPE 가 비어 있으면 재무계획 숫자로 채운다 — 이미 admin 에서 쓴 PPE 는 admin 이 거절한다 */}
                {plan?.ppe && (
                  <ConfirmButton onConfirm={onFillPpe} disabled={saving} ask="재무계획의 입금 일정 · 투입인건비 · 비용으로 admin PPE 를 채웁니다(비어 있을 때만)"
                    className="px-3 py-2 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                    PPE 채우기
                  </ConfirmButton>
                )}
                <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                  <CheckCircle2 size={14} /> 반영됨
                </span>
              </span>
            )}
          </div>
          {!registered && (
            <div className="px-4 py-3 space-y-3 text-sm">
              <div className="grid gap-2 sm:grid-cols-4">
                <label className="text-xs text-slate-500">시작일
                  <input type="date" value={ppe.startDate} onChange={(e) => setPpe({ ...ppe, startDate: e.target.value })}
                    className="mt-1 w-full px-2 py-1 text-sm border border-slate-200 rounded focus:border-indigo-400 focus:outline-none" />
                </label>
                <label className="text-xs text-slate-500">종료일
                  <input type="date" value={ppe.endDate} onChange={(e) => setPpe({ ...ppe, endDate: e.target.value })}
                    className="mt-1 w-full px-2 py-1 text-sm border border-slate-200 rounded focus:border-indigo-400 focus:outline-none" />
                </label>
                <label className="text-xs text-slate-500">PM
                  <input value={ppe.pm} onChange={(e) => setPpe({ ...ppe, pm: e.target.value })}
                    className="mt-1 w-full px-2 py-1 text-sm border border-slate-200 rounded focus:border-indigo-400 focus:outline-none" />
                </label>
                <label className="text-xs text-slate-500">계약금액 (만원)
                  <input type="number" min={0} value={ppe.contractValue}
                    onChange={(e) => setPpe({ ...ppe, contractValue: Number(e.target.value) })}
                    className="mt-1 w-full px-2 py-1 text-sm text-right border border-slate-200 rounded focus:border-indigo-400 focus:outline-none tabular-nums" />
                  {/* 원 · 만원이 한 화면에 섞여 있다 — 원으로 다시 보여 준다 */}
                  <span className="mt-0.5 block text-right text-[11px] text-slate-400 tabular-nums">= {won(ppe.contractValue * 10000)}</span>
                </label>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0">
                  admin 에 이미 있으면
                  <select value={linkId} onChange={(e) => setLinkId(e.target.value)}
                    className="max-w-[260px] px-2 py-1 text-sm border border-slate-200 rounded focus:border-indigo-400 focus:outline-none">
                    <option value="">{candidates === null ? '불러오는 중…' : candidates.length ? 'admin 프로젝트 고르기' : '이을 admin 프로젝트 없음'}</option>
                    {(candidates ?? []).filter((c) => c.ppeAt).map((c) => (
                      <option key={c.id} value={c.id}>#{c.id} {c.name} ({c.startDate?.slice(0, 7)})</option>
                    ))}
                  </select>
                  <ConfirmButton onConfirm={() => onRegisterPpe({ adminProjectId: Number(linkId) })} disabled={saving || !linkId}
                    ask={`한 번 더 — admin #${linkId} 에 잇습니다`}
                    armedClassName="px-3 py-1 rounded bg-emerald-600 text-white text-xs font-medium whitespace-nowrap"
                    className="px-3 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                    잇기
                  </ConfirmButton>
                </div>
                {/* admin 에 프로젝트 · PPE 가 생긴다 — 무엇으로 만드는지 한 번 더 보인다(#108) */}
                <ConfirmButton
                  onConfirm={() => onRegisterPpe(ppe)}
                  disabled={saving || !canCreate}
                  ask={`한 번 더 — admin 에 ${ppe.contractValue.toLocaleString()}만원 · ${ppe.startDate} ~ ${ppe.endDate} · PM ${ppe.pm} 으로 만듭니다`}
                  armedClassName="flex-shrink-0 px-4 py-2 rounded-lg text-xs font-medium bg-emerald-700 text-white"
                  className="flex-shrink-0 px-5 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  PPE 반영
                </ConfirmButton>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

const BidResultPanel: React.FC<{ project: Project; onCheck: () => Promise<void>; saving: boolean }> = ({ project, onCheck, saving }) => {
  const r = project.bidResult;
  const tone = r?.state === '수주' ? 'text-emerald-700' : r?.state === '실주' ? 'text-red-600' : 'text-slate-500';
  return (
    <section className="bg-white rounded-lg border border-slate-200">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">나라장터 결과</h2>
        <button onClick={onCheck} disabled={saving}
          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          지금 확인
        </button>
      </div>
      <div className="px-4 py-3 grid gap-2 sm:grid-cols-4 text-sm">
        <div><div className="text-xs text-slate-500">결과</div><div className={`font-semibold ${tone}`}>{r ? (r.state === '대기' ? `대기 · ${r.progress ?? '개찰 전'}` : r.state) : '아직 확인 안 함'}</div></div>
        <div><div className="text-xs text-slate-500">낙찰</div><div className="text-slate-800">{r?.winner ?? '—'}</div></div>
        <div><div className="text-xs text-slate-500">낙찰금액</div><div className="tabular-nums text-slate-800">{won(r?.amount)}</div></div>
        <div><div className="text-xs text-slate-500">확인</div><div className="text-xs text-slate-500">{r?.checkedAt ? new Date(r.checkedAt).toLocaleString('ko-KR') : '—'}{r?.finalDate ? ` · 최종낙찰 ${r.finalDate}` : ''}</div></div>
      </div>
      {r?.manual && (() => {
        const nara = r.state === '수주' || r.state === '실주' ? r.state : null;
        const same = nara === r.manual.outcome;
        return (
          <p className={`px-4 pb-3 text-xs ${!nara ? 'text-slate-500' : same ? 'text-emerald-700' : 'text-red-600 font-medium'}`}>
            직접 판정 — {r.manual.outcome}{r.manual.amount ? ` · ${won(r.manual.amount)}` : ''} · {r.manual.by} · {new Date(r.manual.at).toLocaleString('ko-KR')}
            {!nara ? ' · 나라장터 결과가 나오면 여기서 견줍니다' : same ? ' · 나라장터 결과와 같습니다' : ` · ⚠️ 나라장터 결과(${nara})와 다릅니다 — 확인하세요`}
          </p>
        );
      })()}
      {r?.ppe && (
        <p className={`px-4 pb-3 text-xs ${r.ppe.state === '반영' || r.ppe.state === '이미 반영' ? 'text-emerald-700' : 'text-amber-700'}`}>
          PPE {r.ppe.state}{r.ppe.missing?.length ? ` — ${r.ppe.missing.join(' · ')} 를 채우면 아래 「PPE 반영」으로` : ''}{r.ppe.error ? ` — ${r.ppe.error}` : ''}{r.ppe.adminProjectId ? ` · admin #${r.ppe.adminProjectId}` : ''}
        </p>
      )}
    </section>
  );
};
